import React, { forwardRef, useCallback } from 'react';
import { ModalBody } from '@atlaskit/modal-dialog';
import { useUiModificationsEnabledStatus } from '@atlassian/jira-issue-adjustments/src/controllers/adjustments-context/hooks/use-enabled-status/index.tsx';
import { ISSUE_CREATE_MODAL_BODY_SELECTOR } from '@atlassian/jira-issue-create-commons/src/common/utils/select/index.tsx';
import { useFormContext } from '@atlassian/jira-issue-create-form-context';
import { useMinimizableModal } from '@atlassian/jira-minimizable-modal/src/controllers/use-minimizable-modal/index.tsx';
import type {
	ModalContainerProps,
	MinimizableModalProps,
} from '@atlassian/jira-minimizable-modal/src/types';
import { Modal } from '@atlassian/jira-minimizable-modal/src/ui/modal-container/modal/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import Footer from '../../../../common/ui/footer';
import { useMinimizableModalWrapperProps } from '../../../../controllers/minimizable-modal-wrapper-props/main.tsx';
import Header from './header';

const HeaderComponent = () => {
	const { shouldShowChangerSpotlight, dismissModalEntryPointWrapper } =
		useMinimizableModalWrapperProps();

	return (
		<Header
			shouldDisableActions={false}
			shouldShowChangerSpotlight={shouldShowChangerSpotlight}
			dismissModalEntryPointWrapper={dismissModalEntryPointWrapper}
		/>
	);
};

const ModalBodyNext = forwardRef((props, ref) => (
	<ModalBody>
		{/* @ts-expect-error - TS2322 - Type '((instance: unknown) => void) | MutableRefObject<unknown> | null' is not assignable to type 'LegacyRef<HTMLDivElement> | undefined'. */}
		<div id={ISSUE_CREATE_MODAL_BODY_SELECTOR} ref={ref} {...props} />
	</ModalBody>
));

const FooterComponent = () => {
	const {
		onModalCloseWithConfirm,
		onSubmit,
		isSubmitting,
		isOperationInProgress,
		canCreateAnotherIssue,
		onCreateAnotherChange,
		getShouldDisableSubmit,
		createdIssues,
		displayCreateAnotherIssueToggle,
		showIssuesCreated,
		dismissModalEntryPointWrapper,
	} = useMinimizableModalWrapperProps();
	const shouldDisableSubmit = getShouldDisableSubmit();
	return (
		<Footer
			onClose={onModalCloseWithConfirm('cancel')}
			onSubmit={onSubmit}
			isSubmitting={isSubmitting}
			isOperationInProgress={isOperationInProgress}
			canCreateAnotherIssue={canCreateAnotherIssue}
			onCreateAnotherChange={onCreateAnotherChange}
			shouldDisableSubmit={shouldDisableSubmit}
			createdIssues={createdIssues}
			displayCreateAnotherIssueToggle={displayCreateAnotherIssueToggle}
			showIssuesCreated={showIssuesCreated}
			dismissModalEntryPointWrapper={dismissModalEntryPointWrapper}
		/>
	);
};

export const MinimizableModalModal = ({
	'data-testid': dataTestId,
	children,
}: ModalContainerProps) => {
	const [
		{
			isFormDirty,
			ecosystem: {
				forge: { hasFinishedLoading },
			},
		},
	] = useFormContext();
	const uiModificationsStatus = useUiModificationsEnabledStatus(hasFinishedLoading);

	const { setView, shouldDisableViewChangerButtons, onClose } = useMinimizableModal();

	const { createAnalyticsEvent } = useAnalyticsEvents();
	const onModalCloseOverride = useCallback<NonNullable<MinimizableModalProps['onClose']>>(
		(event, analyticsEvent) => {
			const isEscapePress =
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				(event as KeyboardEvent)?.key === 'Escape' ||
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				(event as KeyboardEvent)?.key === 'Esc' ||
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				(event as KeyboardEvent)?.keyCode === 27;
			const isBlanketClick = !isEscapePress; // Assume it is a blanket click if it isn't an esc event

			if (isBlanketClick && (!hasFinishedLoading || uiModificationsStatus !== 'disabled')) {
				return;
			}

			if (isBlanketClick || isFormDirty) {
				setView('mini');
				const dockAnalyticsEvent = createAnalyticsEvent({
					action: 'docked',
					actionSubject: 'modalDialog',
				});
				fireUIAnalytics(dockAnalyticsEvent, 'modalDialog docked', {
					isBlanketClick,
				});
			} else {
				onClose?.(event, analyticsEvent);
			}
		},
		[
			createAnalyticsEvent,
			hasFinishedLoading,
			isFormDirty,
			onClose,
			setView,
			uiModificationsStatus,
		],
	);

	return (
		<Modal
			data-testid={dataTestId}
			components={{
				Header: HeaderComponent,
				Footer: FooterComponent,
				// @ts-expect-error - TS2322 - Type 'ForwardRefExoticComponent<RefAttributes<unknown>>' is not assignable to type 'ComponentType<ModalBodyProps> | undefined'.
				Body: ModalBodyNext,
			}}
			onClose={!shouldDisableViewChangerButtons ? onModalCloseOverride : undefined}
			shouldCloseOnOverlayClick={!shouldDisableViewChangerButtons}
		>
			{children}
		</Modal>
	);
};
